import { useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { LayoutTemplate, PanelLeft, Heart, Minus, Plus } from "lucide-react";

export default function CombatHUD({ gameId, character, stats, combat }) {
  const [position, setPosition] = useState(() => localStorage.getItem("combat_hud_position") || "top");

  if (!combat?.active || !character) return null;

  const isPaper = character.mode !== "full";
  const maxHp = isPaper ? character.hp_max ?? 10 : stats?.max_hp ?? 0;
  const curHp = character.hp_current ?? maxHp;

  const setPos = (p) => {
    setPosition(p);
    localStorage.setItem("combat_hud_position", p);
  };

  const adjustHp = async (delta) => {
    const next = Math.max(0, Math.min(maxHp, curHp + delta));
    await api.patch(`/characters/${character.id}`, { hp_current: next });
  };

  const rollAttack = async (label, modifier) => {
    try {
      await api.post(`/characters/${character.id}/roll`, { game_id: gameId, label, modifier });
    } catch {
      toast.error("Roll failed");
    }
  };

  const rollPreset = async (preset) => {
    try {
      await api.post(`/characters/${character.id}/paper-roll`, { game_id: gameId, die: `1${preset.die}`, bonuses: preset.bonuses || [], label: preset.label });
    } catch {
      toast.error("Roll failed");
    }
  };

  const actions = isPaper
    ? (character.paper_presets || []).map((p) => ({ key: p.id, label: p.label, sub: p.die, onClick: () => rollPreset(p) }))
    : [
        ...(stats?.strikes || []).map((s) => ({ key: s.slug, label: s.name, sub: `${s.attack_bonus >= 0 ? "+" : ""}${s.attack_bonus}`, onClick: () => rollAttack(`${s.name} Attack`, s.attack_bonus) })),
        ...(stats?.known_spells || []).map((sp) => ({ key: sp.slug, label: sp.name, sub: `Lv${sp.level}`, onClick: () => rollAttack(`Cast ${sp.name}`, stats.spellcasting?.attack ?? 0) })),
      ];

  const hpBadge = (
    <div className="flex items-center gap-2 bg-black/50 rounded-full px-3 py-1.5 border border-white/10 shrink-0" data-testid="combat-hud-hp">
      <Heart className="w-3.5 h-3.5 text-[#D90429]" />
      <button data-testid="combat-hud-hp-minus" onClick={() => adjustHp(-1)} className="text-[#f1e0c5]/40 hover:text-[#D90429]"><Minus className="w-3 h-3" /></button>
      <span className="text-sm font-mono-sheet text-[#f1e0c5]">{curHp}<span className="text-[#f1e0c5]/30">/{maxHp}</span></span>
      <button data-testid="combat-hud-hp-plus" onClick={() => adjustHp(1)} className="text-[#f1e0c5]/40 hover:text-[#4d7c0f]"><Plus className="w-3 h-3" /></button>
    </div>
  );

  const posToggle = (
    <button
      data-testid="combat-hud-position-toggle"
      onClick={() => setPos(position === "top" ? "side" : "top")}
      title={position === "top" ? "Move to side" : "Move to top"}
      className="w-7 h-7 rounded-full flex items-center justify-center text-[#f1e0c5]/40 hover:text-[#D4AF37] shrink-0"
    >
      {position === "top" ? <PanelLeft className="w-3.5 h-3.5" /> : <LayoutTemplate className="w-3.5 h-3.5" />}
    </button>
  );

  if (position === "side") {
    return (
      <div className="absolute top-20 left-4 bottom-40 z-20 w-40 flex flex-col gap-2" data-testid="combat-hud-side">
        <div className="flex items-center justify-between">{hpBadge}{posToggle}</div>
        <div className="flex-1 overflow-y-auto scrollbar-thin space-y-1.5 bg-black/40 backdrop-blur-2xl border border-white/10 rounded-xl p-2">
          {actions.length === 0 && <p className="text-[10px] text-[#f1e0c5]/30 text-center py-2">No attacks set up yet.</p>}
          {actions.map((a) => (
            <button key={a.key} data-testid={`combat-hud-action-${a.key}`} onClick={a.onClick} className="w-full text-left px-2.5 py-1.5 rounded-lg bg-black/30 hover:bg-[#D90429]/20 transition-colors duration-200">
              <p className="text-xs text-[#f1e0c5] truncate">{a.label}</p>
              <p className="text-[10px] text-[#D4AF37]">{a.sub}</p>
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="absolute top-16 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2 max-w-[92vw] md:max-w-xl bg-black/50 backdrop-blur-2xl border border-white/10 rounded-full px-2 py-1.5" data-testid="combat-hud-top">
      {hpBadge}
      <div className="flex items-center gap-1.5 overflow-x-auto scrollbar-thin">
        {actions.length === 0 && <p className="text-[10px] text-[#f1e0c5]/30 px-2 whitespace-nowrap">No attacks set up yet</p>}
        {actions.map((a) => (
          <button key={a.key} data-testid={`combat-hud-action-${a.key}`} onClick={a.onClick} className="shrink-0 px-3 py-1.5 rounded-full bg-black/30 hover:bg-[#D90429]/20 transition-colors duration-200 whitespace-nowrap">
            <span className="text-xs text-[#f1e0c5]">{a.label}</span> <span className="text-[10px] text-[#D4AF37]">{a.sub}</span>
          </button>
        ))}
      </div>
      {posToggle}
    </div>
  );
}
